import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, ClickAwayListener, Grid, Tooltip, Typography, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import theme from "../../elements/Theme";
import { pricing } from "./pricing";

export interface IconTypographyI {
  text: string;
}

const IconTypography = ({ text }: IconTypographyI) => (
  <Typography
    variant={"h5"}
    fontWeight={400}
    mx="auto"
    sx={{ display: "inline-flex", alignItems: "center" }}
  >
    <FiberManualRecordRoundedIcon
      fontSize="large"
      sx={{
        width: "0.9em",
        height: "0.9em",
        color: theme.palette.secondary.main,
      }}
    />
    {text}
    <br />
  </Typography>
);

const PricingFrame: React.FC = () => {
  const { t } = useTranslation();

  let [year, setYear] = useState("year1");
  const [animate, setAnimate] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const textColor = theme.palette.text;
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleClick = (selectedYear: string) => {
    setAnimate(true);
    const timer = setTimeout(() => {
      setAnimate(false);
      setYear(selectedYear);
    }, 300);
    return () => clearTimeout(timer);
  };

  const years = ["year1", "year2", "year3", "year4"];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        px: isMobile ? "20px" : "73px",
        display: "flex",
        flexDirection: "column",
        mb: isMobile ? "10vh" : "20vh",
      }}
    >
      <Typography
        variant="h3"
        mx="auto"
        textAlign="center"
        mb="3rem"
        fontFamily="Open Sans Bold"
        color={theme.palette.secondary.main}
      >
        {t("PricingFrame.title")}
      </Typography>
      <Box display="flex" flexDirection="row" justifyContent="space-evenly" flexWrap="wrap" gap={1}>
        {years.map((y) => (
          <Button
            key={y}
            sx={{
              borderRadius: "53px",
              backgroundColor:
                year === y ? textColor.disabled : textColor.secondary,
              paddingLeft: "20px",
              paddingRight: "20px",
              "&:hover": {
                backgroundColor:
                  year === y ? textColor.disabled : textColor.secondary,
              },
            }}
            onClick={() => handleClick(y)}
          >
            <Typography
              variant="h3"
              color={year === y ? textColor.secondary : textColor.disabled}
              sx={{
                transition:
                  "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
              }}
            >
              {t(`PricingFrame.${y}`)}
            </Typography>
          </Button>
        ))}
      </Box>
      <Typography
        variant="h3"
        mx="auto"
        mt={isMobile ? "2.5rem" : "4.5rem"}
        fontFamily="Open Sans Bold"
      >
        {t(`PricingFrame.note${year.charAt(0).toUpperCase() + year.slice(1)}`)}
      </Typography>
      <Typography
        variant="h5"
        mx="auto"
        textAlign="center"
        mb={isMobile ? "2.5rem" : "4.5rem"}
      >
        {t(`PricingFrame.equivalent${year.charAt(0).toUpperCase() + year.slice(1)}`)}
      </Typography>
      <Box display="flex" justifyContent="center">
        <Grid
          container
          spacing={2}
          justifyContent="center"
          xs={12}
          md={10}
          lg={8}
          sx={{
            opacity: animate ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {Object.values(pricing[year]).map((price, index) => (
            <Grid
              item
              xs={12}
              md={6}
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <Typography variant="h3" fontFamily="Open Sans Bold" gutterBottom>
                {t(price.title)}
              </Typography>
              {price.title !== "Optional lessons:" &&
              price.title !== "Optional offer:"
                ? price.text.map((line, lineIndex) => (
                    <Typography
                      key={lineIndex}
                      variant="h5"
                      fontWeight={400}
                      sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                      <FiberManualRecordRoundedIcon
                        fontSize="large"
                        sx={{ color: theme.palette.secondary.main }}
                      />
                      {t(line)}
                    </Typography>
                  ))
                : price.text.map((line, lineIndex) => (
                    <Typography
                      key={lineIndex}
                      variant="h5"
                      fontWeight={400}
                      sx={{ display: "inline-flex", alignItems: "center" }}
                    >
                      <AddIcon
                        fontSize="large"
                        sx={{ color: theme.palette.secondary.main }}
                      />
                      {t(line)}
                    </Typography>
                  ))}
            </Grid>
          ))}
        </Grid>
      </Box>
      <Box
        mx="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
        mt="5vh"
        mb="2vh"
      >
        <Box display="flex" flexWrap="wrap" justifyContent="center" mb="1vh">
          <Typography variant="h3" fontWeight={400}>
            {t("PricingFrame.tuition")}
          </Typography>
          &nbsp;
          <Typography
            variant="h3"
            color={textColor.disabled}
            fontFamily="Open Sans Bold"
          >
            {t(`PricingFrame.price${year.charAt(0).toUpperCase() + year.slice(1)}`)}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" alignItems="center">
          <Typography variant="h3" fontFamily="Open Sans Bold" color="green">
            {t("PricingFrame.discount20")}
          </Typography>
          <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
            <Tooltip
              title={
                <Typography variant="body1" sx={{ p: 0.5 }}>
                  {t("PricingFrame.discountTooltip")}
                </Typography>
              }
              open={tooltipOpen}
              onClose={() => setTooltipOpen(false)}
              onOpen={() => !isMobile && setTooltipOpen(true)}
              disableFocusListener={isMobile}
              disableHoverListener={isMobile}
              disableTouchListener={isMobile}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "#224A85",
                    color: "#fff",
                    fontSize: "14px",
                    borderRadius: "8px",
                    maxWidth: 280,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                },
                arrow: {
                  sx: { color: "#224A85" },
                },
              }}
            >
              <InfoOutlinedIcon
                onClick={() => setTooltipOpen(!tooltipOpen)}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: "1.2rem",
                  ml: 0.5,
                  cursor: "pointer",
                  "&:hover": { opacity: 0.7 },
                }}
              />
            </Tooltip>
          </ClickAwayListener>
          &nbsp;
          <Typography
            variant="h3"
            color={textColor.disabled}
            fontFamily="Open Sans Bold"
          >
            {t(`PricingFrame.price${year.charAt(0).toUpperCase() + year.slice(1)}discount20`)}
          </Typography>
          &nbsp;
          <Typography variant="h3" fontFamily="Open Sans Bold">
            {t("PricingFrame.untilApril")}
          </Typography>
        </Box>
        <Box display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" alignItems="center" mt="0.5vh">
          <Typography variant="h3" fontFamily="Open Sans Bold" color="green">
            {t("PricingFrame.discount10")}
          </Typography>
          <Tooltip
            title={
              <Typography variant="body1" sx={{ p: 0.5 }}>
                {t("PricingFrame.discountTooltip")}
              </Typography>
            }
            arrow
            enterTouchDelay={0}
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#224A85",
                  color: "#fff",
                  fontSize: "14px",
                  borderRadius: "8px",
                  maxWidth: 280,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              },
              arrow: {
                sx: { color: "#224A85" },
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                color: theme.palette.secondary.main,
                fontSize: "1.2rem",
                ml: 0.5,
                cursor: "pointer",
                "&:hover": { opacity: 0.7 },
              }}
            />
          </Tooltip>
          &nbsp;
          <Typography
            variant="h3"
            color={textColor.disabled}
            fontFamily="Open Sans Bold"
          >
            {t(`PricingFrame.price${year.charAt(0).toUpperCase() + year.slice(1)}discount10`)}
          </Typography>
          &nbsp;
          <Typography variant="h3" fontFamily="Open Sans Bold">
            {t("PricingFrame.untilMay")}
          </Typography>
        </Box>
      </Box>
      <Box mb="6vh" mt="4vh" sx={{ display: "flex", flexDirection: "column" }}>
        <IconTypography text={t("PricingFrame.iconText1")} />
        <IconTypography text={t("PricingFrame.iconText2")} />
        <IconTypography text={t("PricingFrame.iconText3")} />
        <IconTypography text={t("PricingFrame.iconText4")} />
      </Box>
      <Box display="flex" justifyContent="center" width="100%">
        <Grid
          container
          justifyContent="center"
          spacing={2}
          xs={12}
          md={10}
          lg={8}
        >
          <Grid item xs={12}>
            <Typography variant="h3" fontFamily="Open Sans Bold" gutterBottom>
              {t("PricingFrame.optionalFees")}
            </Typography>
          </Grid>
          {Object.values(
            pricing[year === "year1" ? "optional1" : "optional2"]
          ).map((price, index) => (
            <Grid
              item
              xs={12}
              key={index}
              alignItems="center"
              justifyContent="center"
            >
              <Typography
                variant="h5"
                fontWeight={600}
                sx={{ color: theme.palette.secondary.main }}
              >
                {t(price.title)}
              </Typography>
              {price.text.map((line, lineIndex) => (
                <Typography key={lineIndex} variant="h5" fontWeight={400}>
                  {t(line)}
                </Typography>
              ))}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default PricingFrame;
